import { useState } from "react";
import { Play, Square, RotateCw, Trash2, Heart } from "lucide-react";
import {
  useLiveContainers,
  useLiveContainerAction,
  useDeleteLiveContainer,
} from "@/hooks/useDocker";
import type { LiveContainer, ContainerAction } from "@/types/docker";
import { ContainerDetailDrawer } from "./ContainerDetailDrawer";

function stateColor(s: string): string {
  if (s === "running") return "bg-green-900/40 text-green-300";
  if (s === "exited" || s === "dead") return "bg-red-900/40 text-red-300";
  if (s === "paused") return "bg-yellow-900/40 text-yellow-300";
  return "bg-gray-800 text-gray-400";
}

function healthColor(h: string | null): string {
  if (h === "healthy") return "text-green-400";
  if (h === "unhealthy") return "text-red-400";
  return "text-gray-500";
}

function portsLabel(c: LiveContainer): string {
  const published = c.ports.filter((p) => p.host_port);
  if (published.length === 0) return "—";
  return published
    .map((p) => `${p.host_port}→${p.container_port}/${p.protocol}`)
    .join(", ");
}

export function LiveContainersPanel({ hostId }: { hostId: number }) {
  const { data: containers, isLoading } = useLiveContainers(hostId);
  const action = useLiveContainerAction(hostId);
  const del = useDeleteLiveContainer(hostId);
  const [selected, setSelected] = useState<LiveContainer | null>(null);

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-200">
        Conteneurs{" "}
        <span className="text-sm font-normal text-gray-500">
          (live · {containers?.length ?? 0})
        </span>
      </h2>

      <div className="overflow-hidden rounded-lg border border-gray-800">
        <table className="min-w-full divide-y divide-gray-800 text-sm">
          <thead className="bg-gray-900/60 text-left text-gray-400">
            <tr>
              <th className="px-4 py-2">Nom</th>
              <th className="px-4 py-2">Image</th>
              <th className="px-4 py-2">État</th>
              <th className="px-4 py-2">Ports</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {(containers ?? []).map((c) => {
              const running = c.state === "running";
              return (
                <tr
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className="cursor-pointer hover:bg-gray-900/40"
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-gray-100">{c.name}</span>
                      {!c.managed && (
                        <span
                          title="Conteneur non géré par AlphaProx (présent sur l'hôte)"
                          className="rounded bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-400"
                        >
                          externe
                        </span>
                      )}
                      {c.compose_project && (
                        <span className="rounded bg-indigo-900/40 px-1.5 py-0.5 text-[10px] text-indigo-300">
                          {c.compose_project}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="max-w-[16rem] truncate px-4 py-2 text-gray-400">
                    {c.image}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${stateColor(
                          c.state ?? c.status,
                        )}`}
                      >
                        {c.state ?? c.status}
                      </span>
                      {c.health && (
                        <span
                          title={`health: ${c.health}`}
                          className={healthColor(c.health)}
                        >
                          <Heart className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-400">
                    {portsLabel(c)}
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5 text-gray-400">
                      {(["start", "restart", "stop"] as ContainerAction[]).map(
                        (a) => (
                          <button
                            key={a}
                            title={a}
                            disabled={
                              (a === "start" && running) ||
                              (a !== "start" && !running)
                            }
                            onClick={() =>
                              action.mutate({ cid: c.id, action: a })
                            }
                            className="rounded p-1 hover:bg-gray-800 hover:text-gray-100 disabled:opacity-30"
                          >
                            {a === "start" ? (
                              <Play className="h-4 w-4" />
                            ) : a === "stop" ? (
                              <Square className="h-4 w-4" />
                            ) : (
                              <RotateCw className="h-4 w-4" />
                            )}
                          </button>
                        ),
                      )}
                      <button
                        title="Supprimer"
                        onClick={() => {
                          if (window.confirm(`Supprimer le conteneur ${c.name} ?`))
                            del.mutate(c.id);
                        }}
                        className="rounded p-1 hover:bg-red-900/40 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && containers && containers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-3 text-gray-500">
                  Aucun conteneur sur cet hôte.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <ContainerDetailDrawer
          hostId={hostId}
          container={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}
